import React, { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Mail, Upload, Check, Download, ShoppingBag } from "lucide-react";
import { supabase } from "@/lib/supabase";

// Define the steps in the wizard
const STEPS = [
  {
    id: 1,
    title: "Customer Purchase",
    description: "Customer buys your product on your Shopify store",
    icon: ShoppingBag,
    setupRequired: false,
  },
  {
    id: 2,
    title: "Email Integration",
    description: "Connect your email marketing provider",
    icon: Mail,
    setupRequired: true,
  },
  {
    id: 3,
    title: "Photo Review",
    description: "Review and approve customer photos",
    icon: Check,
    setupRequired: false,
  },
  {
    id: 4,
    title: "Reward Settings",
    description: "Set the discount for approved photos",
    icon: ShoppingBag,
    setupRequired: true,
  },
  {
    id: 5,
    title: "Upload Link",
    description: "Your custom URL for photo submissions",
    icon: Upload,
    setupRequired: false,
  },
];

interface SetupWizardProps {
  onComplete: () => void;
}

// Email service provider options
type EmailProvider = "none" | "klaviyo" | "omnisend" | "sendlane" | "attentive";

const SetupWizard: React.FC<SetupWizardProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    provider: "none" as EmailProvider,
    klaviyoApiKey: "",
    omnisendApiKey: "",
    sendlaneApiKey: "",
    attentiveApiKey: "",
  });
  const [discountAmount, setDiscountAmount] = useState(25);
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  const [uniqueUploadLink, setUniqueUploadLink] = useState("");

  // Load existing settings if available
  useEffect(() => {
    const loadExistingSettings = async () => {
      try {
        // Get the current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.error("No user found");
          return;
        }
        
        // Load brand settings
        const { data, error } = await supabase
          .from("brands")
          .select("klaviyo_api_key, omnisend_api_key, sendlane_api_key, attentive_api_key, ugc_discount_amount, unique_upload_slug")
          .eq("id", user.id)
          .single();
        
        if (error) {
          console.error("Error loading brand settings:", error);
          return;
        }
        
        // Update state with existing settings
        if (data) {
          setDiscountAmount(data.ugc_discount_amount || 25);
          
          // Get the upload link
          setUniqueUploadLink(`${window.location.origin}/upload/${data.unique_upload_slug}`);
          
          // Determine which email provider is configured
          let provider: EmailProvider = "none";
          if (data.klaviyo_api_key) provider = "klaviyo";
          else if (data.omnisend_api_key) provider = "omnisend";
          else if (data.sendlane_api_key) provider = "sendlane";
          else if (data.attentive_api_key) provider = "attentive";
          
          setEmailSettings({
            provider,
            klaviyoApiKey: data.klaviyo_api_key || "",
            omnisendApiKey: data.omnisend_api_key || "",
            sendlaneApiKey: data.sendlane_api_key || "",
            attentiveApiKey: data.attentive_api_key || "",
          });
        }
        
        setHasLoadedSettings(true);
      } catch (error) {
        console.error("Error loading settings:", error);
        setHasLoadedSettings(true);
      }
    };
    
    loadExistingSettings();
  }, []);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error("No user found");
        return;
      }

      // Prepare update data based on selected email provider
      const updateData: Record<string, any> = {
        ugc_discount_amount: discountAmount,
        setup_completed: true,
        klaviyo_api_key: null,
        omnisend_api_key: null,
        sendlane_api_key: null,
        attentive_api_key: null,
      };

      // Only set the API key for the selected provider
      if (emailSettings.provider === "klaviyo") {
        updateData.klaviyo_api_key = emailSettings.klaviyoApiKey;
      } else if (emailSettings.provider === "omnisend") {
        updateData.omnisend_api_key = emailSettings.omnisendApiKey;
      } else if (emailSettings.provider === "sendlane") {
        updateData.sendlane_api_key = emailSettings.sendlaneApiKey;
      } else if (emailSettings.provider === "attentive") {
        updateData.attentive_api_key = emailSettings.attentiveApiKey;
      }

      // Update user settings with the collected information
      const { error } = await supabase
        .from("brands")
        .update(updateData)
        .eq("id", user.id);

      if (error) {
        console.error("Error updating brand settings:", error);
        return;
      }

      // Setup complete!
      onComplete();
    } catch (error) {
      console.error("Error completing setup:", error);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching settings
  if (!hasLoadedSettings) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Render current step content
  const renderStepContent = () => {
    const step = STEPS[currentStep];

    switch (step.id) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-center mb-4 bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">Welcome to Your Photo Collection Workflow</h3>
            <p className="text-center text-gray-600">
              Your platform is ready to collect authentic customer photos that 
              drive sales and build trust with new customers.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-lg border border-teal-100 mt-4 shadow-sm">
              <p className="text-sm text-center font-medium text-gray-700">
                Let's go through a quick setup to get everything configured!
              </p>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-center mb-4 bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">Connect Your Email Marketing Provider</h3>
            <p className="text-center text-gray-600 mb-6">
              Select your email provider to send discount codes when photos are approved.
            </p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Select Your Email Provider</label>
                <div className="relative">
                  <select
                    value={emailSettings.provider}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      provider: e.target.value as EmailProvider
                    })}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm 
                              focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-base"
                  >
                    <option value="none">None (Manual Email Delivery)</option>
                    <option value="klaviyo">Klaviyo</option>
                    <option value="omnisend">Omnisend</option>
                    <option value="sendlane">Sendlane</option>
                    <option value="attentive">Attentive</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {emailSettings.provider === "klaviyo" && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700">
                    Klaviyo API Key
                  </label>
                  <input
                    type="text"
                    value={emailSettings.klaviyoApiKey}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      klaviyoApiKey: e.target.value
                    })}
                    placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Find this in your Klaviyo account settings under API Keys.
                  </p>
                </div>
              )}
              
              {emailSettings.provider === "omnisend" && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700">
                    Omnisend API Key
                  </label>
                  <input
                    type="text"
                    value={emailSettings.omnisendApiKey}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      omnisendApiKey: e.target.value
                    })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              )}
              
              {emailSettings.provider === "sendlane" && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700">
                    Sendlane API Key
                  </label>
                  <input
                    type="text"
                    value={emailSettings.sendlaneApiKey}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      sendlaneApiKey: e.target.value
                    })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              )}
              
              {emailSettings.provider === "attentive" && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700">
                    Attentive API Key
                  </label>
                  <input
                    type="text"
                    value={emailSettings.attentiveApiKey}
                    onChange={(e) => setEmailSettings({
                      ...emailSettings,
                      attentiveApiKey: e.target.value
                    })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              )}
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-center mb-4 bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">Review Customer Photos</h3>
            <p className="text-center text-gray-600">
              Your customers will submit photos that you can review in your dashboard.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-lg border border-teal-100 mt-4 shadow-sm">
              <div className="flex flex-col items-center">
                <Check className="h-8 w-8 mb-2 text-teal-600" />
                <p className="text-sm text-center font-medium text-gray-700">
                  When you approve photos, customers will automatically receive 
                  their discount code via email
                </p>
              </div>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-center mb-4 bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">Set Discount Amount</h3>
            <p className="text-center text-gray-600 mb-6">
              Choose how much discount to give customers when their photos are approved.
            </p>
            
            <div className="max-w-xs mx-auto">
              <label className="block text-sm font-medium text-gray-700 text-center">
                Discount Amount ($)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(parseInt(e.target.value) || 0)}
                className="mt-1 block w-full px-4 py-4 border border-gray-300 rounded-lg shadow-sm 
                          focus:outline-none focus:ring-teal-500 focus:border-teal-500 
                          text-center text-2xl font-bold bg-gradient-to-r from-blue-50 to-teal-50"
              />
              <p className="mt-2 text-xs text-gray-500 text-center">
                This is the discount amount customers will receive when their photo is approved.
              </p>
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-center mb-4 bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">Your Unique Upload Link</h3>
            <p className="text-center text-gray-600 mb-6">
              Share this link with your customers so they can submit their photos.
            </p>
            
            <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-lg border border-teal-100 mb-6 shadow-sm">
              <div className="flex flex-col items-center">
                <p className="text-sm text-center font-medium text-gray-700 mb-3">
                  Your custom upload link is ready!
                </p>
                <div className="w-full bg-white rounded-lg border border-gray-300 p-3 font-mono text-sm text-center overflow-x-auto">
                  {uniqueUploadLink}
                </div>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(uniqueUploadLink);
                    alert("Link copied to clipboard!");
                  }}
                  className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg text-sm shadow-sm hover:shadow-md transition-all duration-200"
                >
                  Copy Link
                </button>
              </div>
            </div>
            
            <p className="text-center text-gray-600">
              You can also find this link in your dashboard at any time.
            </p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 fixed-height-container">
        <div className="mb-8">
          {/* Logo at the top */}
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.svg" 
              alt="Logo" 
              className="h-10"
              onError={(e) => {
                // Fallback if logo.svg doesn't exist
                e.currentTarget.src = "https://via.placeholder.com/120x40";
              }}
            />
          </div>
          
          <h2 className="text-2xl font-bold text-center bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent">How It Works</h2>
          <p className="mt-1 text-sm text-center text-gray-600">
            A seamless workflow to collect, review and utilize authentic customer photos
          </p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-10">
          <nav className="flex justify-between">
            {STEPS.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(index)}
                className={`flex flex-col items-center ${
                  index <= currentStep ? "text-teal-600" : "text-gray-400"
                }`}
              >
                <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-2 ${
                  index <= currentStep 
                    ? "bg-gradient-to-r from-blue-500 to-teal-500 text-white shadow-md" 
                    : "bg-gray-200"
                }`}>
                  <step.icon size={22} />
                </div>
                <span className="text-xs font-medium">{`Step ${step.id}`}</span>
                <span className={`text-sm font-medium ${
                  index === currentStep ? "bg-gradient-to-r from-blue-500 to-teal-500 bg-clip-text text-transparent" : ""
                }`}>
                  {step.title}
                </span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Step Content */}
        <div className="bg-white shadow-lg overflow-hidden rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="text-lg leading-6 font-medium text-gray-900 text-center">
              {STEPS[currentStep].title}
            </div>
            <p className="mt-1 text-sm text-gray-500 text-center">
              {STEPS[currentStep].description}
            </p>
          </div>
          
          {/* Fixed height content area to prevent layout shifts */}
          <div className="px-6 py-6 min-h-[300px] flex items-center justify-center">
            <div className="w-full">
              {renderStepContent()}
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 flex justify-between">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium transition-all duration-200 ${
                currentStep === 0 
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 hover:shadow"
              }`}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-teal-500 hover:shadow-md transition-all duration-200"
            >
              {currentStep < STEPS.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  {loading ? "Saving..." : "Complete Setup"}
                  {!loading && <Check className="h-4 w-4 ml-2" />}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Add global styles */}
      <style jsx global>{`
        .fixed-height-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          height: auto;
        }
        
        @media (max-width: 768px) {
          .fixed-height-container {
            position: relative;
            top: auto;
            left: auto;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
};

export default SetupWizard; 